import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Euro, CreditCard, Save, XCircle, AlertCircle } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Opportunity, CustomerCompany, PaymentSchedule } from '../../types';
import { paymentScheduleService } from '../../services/paymentScheduleService';

interface PaymentScheduleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
  customer: CustomerCompany;
  onSave: (schedule: PaymentSchedule) => Promise<void>;
}

const PaymentScheduleEditorModal: React.FC<PaymentScheduleEditorModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  customer,
  onSave
}) => {
  useEscapeKey(onClose, isOpen);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  
  // Determine defaults from opportunity
  const defaultRevenueType = opportunity.product?.pricing?.model || 'one_time';
  const defaultTotalValue = opportunity.value || 0;
  const defaultInstallments = defaultRevenueType === 'recurring' 
    ? (opportunity.billingTermMonths || 12) 
    : 1;

  const [formData, setFormData] = useState({
    revenueType: defaultRevenueType as 'recurring' | 'one_time',
    totalValue: defaultTotalValue,
    numberOfMonths: defaultInstallments,
    startMonth: currentMonth,
    notes: `Zahlungsplan für: ${opportunity.title}`
  });

  const [preview, setPreview] = useState<{ month: string; amount: number }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update preview when form data changes
  useEffect(() => {
    if (formData.totalValue > 0 && formData.numberOfMonths > 0) {
      const installments = paymentScheduleService.generateMonthlyInstallments(
        formData.totalValue,
        formData.startMonth,
        formData.numberOfMonths,
        formData.revenueType
      );
      setPreview(installments);
    } else {
      setPreview([]);
    }
  }, [formData]);

  const handleSave = async () => {
    if (formData.totalValue <= 0) {
      setError('Gesamtwert muss größer als 0 sein');
      return;
    }
    if (formData.numberOfMonths <= 0) {
      setError('Anzahl der Monate muss größer als 0 sein');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const schedule = paymentScheduleService.createManualSchedule(
        formData.totalValue,
        formData.revenueType,
        formData.startMonth,
        formData.numberOfMonths,
        opportunity.productId,
        opportunity.product?.name,
        undefined, // userId will be set by the calling component
        formData.notes
      );

      // Add source opportunity ID
      schedule.sourceOpportunityId = opportunity.id;

      await onSave(schedule);
      onClose();
    } catch (err) {
      console.error('Error saving payment schedule:', err);
      setError('Fehler beim Speichern des Zahlungsplans');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const monthlyAmount = formData.numberOfMonths > 0 
    ? formData.totalValue / formData.numberOfMonths 
    : 0;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-bg-raised bg-opacity-75 transition-opacity" 
          onClick={handleSkip}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-bg-card rounded-2xl text-left overflow-hidden shadow-card transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-accent to-accent px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-bg-card bg-opacity-20 rounded-lg">
                  <Calendar className="w-6 h-6 text-txt-inverted" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-txt-inverted">Zahlungsplan erstellen</h3>
                  <p className="text-sm text-info">Passe den Zahlungsplan für diese Opportunity an</p>
                </div>
              </div>
              <button
                onClick={handleSkip}
                className="p-2 hover:bg-bg-card hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-txt-inverted" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Opportunity Info */}
            <div className="bg-info-soft rounded-xl p-4">
              <h4 className="font-semibold text-info mb-2">{opportunity.title}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-accent">Kunde:</span>
                  <span className="ml-2 font-medium text-info">{customer.name}</span>
                </div>
                <div>
                  <span className="text-accent">Produkt:</span>
                  <span className="ml-2 font-medium text-info">{opportunity.product?.name || 'Kein Produkt'}</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-4">
              {/* Revenue Type */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">
                  Revenue-Typ
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, revenueType: 'recurring', numberOfMonths: 12 })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.revenueType === 'recurring'
                        ? 'border-accent bg-info-soft'
                        : 'border-default hover:border-strong'
                    }`}
                  >
                    <CreditCard className={`w-6 h-6 mx-auto mb-2 ${
                      formData.revenueType === 'recurring' ? 'text-accent' : 'text-txt-muted'
                    }`} />
                    <div className="text-center">
                      <div className="font-semibold text-txt">Recurring</div>
                      <div className="text-xs text-txt-secondary">Wiederkehrend</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, revenueType: 'one_time', numberOfMonths: 1 })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.revenueType === 'one_time'
                        ? 'border-accent bg-info-soft'
                        : 'border-default hover:border-strong'
                    }`}
                  >
                    <Euro className={`w-6 h-6 mx-auto mb-2 ${
                      formData.revenueType === 'one_time' ? 'text-accent' : 'text-txt-muted'
                    }`} />
                    <div className="text-center">
                      <div className="font-semibold text-txt">One-Time</div>
                      <div className="text-xs text-txt-secondary">Einmalig</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Total Value */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">
                  Gesamtwert (€)
                </label>
                <input
                  type="number"
                  value={formData.totalValue}
                  onChange={(e) => setFormData({ ...formData, totalValue: Number(e.target.value) })}
                  className="w-full px-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                  placeholder="15000"
                />
              </div>

              {/* Number of Months */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">
                  Anzahl der Raten (Monate)
                </label>
                <input
                  type="number"
                  value={formData.numberOfMonths}
                  onChange={(e) => setFormData({ ...formData, numberOfMonths: Number(e.target.value) })}
                  className="w-full px-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                  placeholder="12"
                  min="1"
                />
                <p className="mt-2 text-sm text-txt-secondary">
                  Monatlicher Betrag: <span className="font-semibold text-txt">{formatCurrency(monthlyAmount)}</span>
                </p>
              </div>

              {/* Start Month */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">
                  Start-Monat
                </label>
                <input
                  type="month"
                  value={formData.startMonth}
                  onChange={(e) => setFormData({ ...formData, startMonth: e.target.value })}
                  className="w-full px-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-2">
                  Notizen (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent"
                  placeholder="Zusätzliche Informationen..."
                />
              </div>
            </div>

            {/* Preview */}
            {preview.length > 0 && (
              <div className="bg-bg-surface rounded-xl p-4">
                <h4 className="font-semibold text-txt mb-3">Vorschau ({preview.length} Raten)</h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {preview.slice(0, 6).map((installment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-bg-card rounded-lg">
                      <span className="text-sm text-txt-secondary">
                        {paymentScheduleService.formatMonthDisplay(installment.month)}
                      </span>
                      <span className="text-sm font-semibold text-txt">
                        {formatCurrency(installment.amount)}
                      </span>
                    </div>
                  ))}
                  {preview.length > 6 && (
                    <div className="text-center text-sm text-txt-tertiary py-2">
                      ... und {preview.length - 6} weitere Raten
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-danger-soft border border-danger rounded-xl p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-danger mt-0.5" />
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-bg-surface px-6 py-4 flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="px-6 py-3 text-txt-secondary hover:text-txt font-medium transition-colors"
            >
              Überspringen
            </button>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSkip}
                className="px-6 py-3 bg-bg-surface-hover text-txt-secondary rounded-xl hover:bg-bg-surface-hover transition-colors font-medium"
              >
                <XCircle className="w-4 h-4 inline mr-2" />
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={saving || formData.totalValue <= 0 || formData.numberOfMonths <= 0}
                className="px-6 py-3 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Speichert...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Zahlungsplan speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentScheduleEditorModal;

